import { useState, useEffect, useCallback } from 'react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import FileInput from '../../../components/FileInput/FileInput';
import { useBulkAddStationChargerRelationToDrResource } from '../../../hooks/queries/resourceRelationQueries';
import './StationBulkAssignModal.css';

/** 시작일·종료일은 YYYY-MM-DD만 데이터 행으로 인정 (엑셀 안내 문구 줄 제외) */
const BULK_CSV_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * 1행 헤더 스킵. 엑셀에서 저장한 .csv는 탭 구분인 경우가 많아 탭을 우선 분리하고,
 * 콤마만 있는 행은 콤마로 분리.
 * 한 데이터 행 → `${계약번호},${시작일},${종료일}` (시작·종료가 날짜 형식일 때만 push)
 */
function parsePowerContractInfoFromCsv(text) {
  if (text == null || typeof text !== 'string') return [];
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length <= 1) return [];

  const result = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const cols = line.includes('\t')
      ? line.split('\t').map((s) => s.trim())
      : line.split(',').map((s) => s.trim());
    const contract = cols[0] ?? '';
    const startDate = cols[1] ?? '';
    const endDate = cols[2] ?? '';
    if (!contract || !startDate || !endDate) continue;
    if (!BULK_CSV_DATE.test(startDate) || !BULK_CSV_DATE.test(endDate)) continue;
    result.push(`${contract},${startDate},${endDate}`);
  }
  return result;
}

/** API 응답 (PascalCase / camelCase 모두 허용) */
function normalizeBulkAssignResponse(raw) {
  if (!raw || typeof raw !== 'object') {
    return { total: 0, passItems: {}, failedItems: {}, errorMsg: null };
  }
  return {
    total: raw.Total ?? raw.total ?? 0,
    passItems: raw.PassItems ?? raw.passItems ?? {},
    failedItems: raw.FailedItems ?? raw.failedItems ?? {},
    errorMsg: raw.ErrorMsg ?? raw.errorMsg ?? null,
  };
}

function objectEntriesSorted(obj) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj)
    .sort()
    .map((k) => [k, obj[k]]);
}

/** 계약번호 → 메시지 배열 아코디언 (성공/실패 구역 공통) */
function BulkAssignResultSection({ title, titleClassName, itemsMap, variant, expandedKeys, onToggle }) {
  const entries = objectEntriesSorted(itemsMap);

  return (
    <div>
      <h4 className={titleClassName || 'station-bulk-assign-result__section-title'}>{title}</h4>
      {entries.length === 0 ? (
        <p className="station-bulk-assign-result__empty">없음</p>
      ) : (
        <div className="station-bulk-assign-result__scroll">
          {entries.map(([contractNo, messages]) => {
            const key = `${variant}:${contractNo}`;
            const open = expandedKeys.has(key);
            const list = Array.isArray(messages) ? messages : messages != null ? [String(messages)] : [];
            return (
              <div key={key} className="station-bulk-assign-result__row">
                <button
                  type="button"
                  className="station-bulk-assign-result__toggle"
                  onClick={() => onToggle(key)}
                  aria-expanded={open}
                >
                  <span
                    className={`station-bulk-assign-result__chevron ${open ? 'station-bulk-assign-result__chevron--open' : ''}`}
                  >
                    <ChevronDownIcon color="var(--gray-600)" width={18} height={18} />
                  </span>
                  <span className="station-bulk-assign-result__contract-no">{contractNo}</span>
                </button>
                {open && (
                  <div className="station-bulk-assign-result__detail">
                    {list.length === 0 ? (
                      <p className="station-bulk-assign-result__detail-line">(내용 없음)</p>
                    ) : (
                      list.map((line, idx) => (
                        <p key={idx} className="station-bulk-assign-result__detail-line">
                          {line}
                        </p>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const StationBulkAssignModal = ({ onClose, drResource, onSuccess }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [parsedPowerContractInfo, setParsedPowerContractInfo] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ phase: 'idle' });
  const [lastRunSummary, setLastRunSummary] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const bulkAddStationChargerRelationToDrResourceMutation = useBulkAddStationChargerRelationToDrResource();
  const drResourceSysId = drResource?.DrResourceSysId;

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!csvFile) {
      setParsedPowerContractInfo([]);
      setParseError('');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== 'string') {
          setParseError('파일 내용을 읽을 수 없습니다.');
          setParsedPowerContractInfo([]);
          return;
        }
        const rows = parsePowerContractInfoFromCsv(text);
        setParsedPowerContractInfo(rows);
        setParseError(
          rows.length === 0
            ? '데이터 행이 없습니다. 1행은 헤더, 2행부터 계량기번호·시작일·종료일(YYYY-MM-DD) 3칸을 채우세요. 엑셀에서 저장한 파일은 탭으로 구분됩니다. 시트 아래 안내 문구 줄은 자동으로 무시됩니다.'
            : ''
        );
      } catch (err) {
        setParseError(err?.message || 'CSV 파싱에 실패했습니다.');
        setParsedPowerContractInfo([]);
      }
    };
    reader.onerror = () => {
      setParseError('파일을 읽을 수 없습니다.');
      setParsedPowerContractInfo([]);
    };
    reader.readAsText(csvFile, 'UTF-8');
  }, [csvFile]);

  const handleProcess = useCallback(async () => {
    if (!drResourceSysId || parsedPowerContractInfo.length === 0 || isProcessing) return;

    setIsProcessing(true);
    setLastRunSummary(null);
    setExpandedKeys(new Set());
    setProgress({ phase: 'requesting' });

    const powerContractInfo = [...parsedPowerContractInfo];

    try {
      const data = await bulkAddStationChargerRelationToDrResourceMutation.mutateAsync({
        drResourceSysId,
        powerContractInfo,
        isDryRun: false,
      });
      setLastRunSummary(normalizeBulkAssignResponse(data));
      onSuccess?.();
    } catch (err) {
      const body = err?.response?.data;
      if (body && typeof body === 'object' && (body.PassItems != null || body.FailedItems != null || body.passItems != null || body.failedItems != null)) {
        setLastRunSummary(normalizeBulkAssignResponse(body));
      } else {
        setLastRunSummary({
          total: 0,
          passItems: {},
          failedItems: {},
          errorMsg: body?.message || body?.Message || err?.message || '요청에 실패했습니다.',
        });
      }
    } finally {
      setIsProcessing(false);
      setProgress({ phase: 'idle' });
    }
  }, [
    drResourceSysId,
    parsedPowerContractInfo,
    isProcessing,
    bulkAddStationChargerRelationToDrResourceMutation,
    onSuccess,
  ]);


  const canStart =
    !!drResourceSysId &&
    parsedPowerContractInfo.length > 0 &&
    !parseError &&
    !isProcessing;

  const passCount = lastRunSummary ? Object.keys(lastRunSummary.passItems || {}).length : 0;
  const failCount = lastRunSummary ? Object.keys(lastRunSummary.failedItems || {}).length : 0;

  return (
    <div className="modal-background">
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>충전소 일괄 할당</h3>
          <button type="button" className="close-btn" onClick={onClose} disabled={isProcessing}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-content my-10 flex flex-col gap-15">
          <p className="text-b2-400 text-gray-600">
            CSV 파일의 <strong>첫 번째 열</strong>에 계량기 계약번호, <strong>두 번째 열</strong>에 정보제공 시작일, <strong>세 번째 열</strong>에 정보제공 종료일을 넣고, <strong>첫 행은 헤더</strong>로 두세요.
            (UTF-8 권장)
          </p>

          {!drResourceSysId && (
            <p className="text-b2-400 text-red-500">DR 자원 정보가 없어 할당할 수 없습니다.</p>
          )}
          <div className="my-5">
            <FileInput
              value={csvFile}
              onChange={setCsvFile}
              accept=".csv,text/csv"
              placeholder="CSV 선택 (1열: 계량기 계약번호, 2열: 정보제공 시작일, 3열: 정보제공 종료일, 1행: 헤더)"
              disabled={isProcessing}
              dragDrop
            />
          </div>

          {parseError && (
            <p className="text-b2-400 text-red-500 whitespace-pre-wrap">{parseError}</p>
          )}

          {parsedPowerContractInfo.length > 0 && (
            <div className="flex flex-col gap-8 my-10">
              <p className="text-b2-700 text-gray-700">
                총 <strong>{parsedPowerContractInfo.length}</strong>건 파싱됨 (요청 시 한 번에 전송)
              </p>
            </div>
          )}

          {isProcessing && (
            <p className="text-b2-400 text-primary">
              {progress.phase === 'requesting' ? '일괄 요청 전송 중…' : '처리 중…'}
            </p>
          )}

          {lastRunSummary != null && !isProcessing && (
            <div className="station-bulk-assign-result text-b2-400 text-gray-700">
              <p>
                처리 건수 <strong className="text-primary">{lastRunSummary.total}</strong> · 성공 항목{' '}
                <strong className="text-primary">{passCount}</strong> · 실패 항목{' '}
                <strong className="text-red-500">{failCount}</strong>
              </p>
              {lastRunSummary.errorMsg ? (
                <p className="station-bulk-assign-result__error-msg">{lastRunSummary.errorMsg}</p>
              ) : null}

              <BulkAssignResultSection
                title="성공 (Pass)"
                itemsMap={lastRunSummary.passItems}
                variant="pass"
                expandedKeys={expandedKeys}
                onToggle={toggleExpanded}
              />

              <BulkAssignResultSection
                title="실패 (Failed)"
                titleClassName="station-bulk-assign-result__section-title station-bulk-assign-result__section-title--fail"
                itemsMap={lastRunSummary.failedItems}
                variant="fail"
                expandedKeys={expandedKeys}
                onToggle={toggleExpanded}
              />
            </div>
          )}

          <div className="flex justify-end gap-10">
            <button type="button" className="btn big-btn btn-style-2" onClick={onClose} disabled={isProcessing}>
              닫기
            </button>
            <button
              type="button"
              className="btn big-btn btn-primary"
              onClick={handleProcess}
              disabled={!canStart}
            >
              {isProcessing ? '처리 중…' : '일괄 처리 시작'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationBulkAssignModal;
